import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { cleanTermText } from './archTerms'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

/**
 * useArchEstimates — fetch all non-archived arch estimates ordered by updated_at DESC.
 */
export function useArchEstimates() {
    const [estimates, setEstimates] = useState([])
    const [loading,   setLoading]   = useState(true)
    const [error,     setError]     = useState(null)
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)
        const cached = await loadReferenceCache('arch_estimates_active', null).catch(() => null)
        if (cached) {
            setEstimates(cached.data)
            if (!silent) setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_estimates')
            .select('*')
            .neq('status', 'archived')
            .order('updated_at', { ascending: false })
        if (id !== loadIdRef.current) return
        if (err) { setError(err.message); if (!cached) setEstimates([]) }
        else     { setEstimates(data || []); await saveReferenceCache('arch_estimates_active', data || [], null).catch(() => {}) }
        if (!silent) setLoading(false)
    }, [])

    const load = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, false)
    }, [fetchAndApply])

    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    useEffect(() => { load() }, [load])

    return { estimates, loading, error, reload: load, silentReload }
}

/**
 * Save (insert if new, update if estimate.id is set) an arch estimate.
 * Field names on the JS object use camelCase; DB columns use snake_case.
 */
export async function saveArchEstimate(estimate, userId) {
    const payload = {
        number:           estimate.number          || null,
        date:             estimate.date             || null,
        client:             estimate.client            || null,
        phone:              estimate.phone             || null,
        email:              estimate.email             || null,
        monday_item_id:     estimate.mondayItemId      || null,
        project_type:       estimate.projectType       || null,
        project_description: estimate.projectDescription || null,
        address:            estimate.address           || null,
        address_details:  estimate.addressDetails   || {},
        market:           estimate.market           || null,
        sqft:             Number(estimate.sqft)     || 0,
        difficulty:       Number(estimate.difficulty) || 1,
        discipline_scope: estimate.disciplineScope  || 'full',
        arch_pct:         Number(estimate.archPct)  || 0,
        struct_pct:       Number(estimate.structPct)|| 0,
        mep_pct:          Number(estimate.mepPct)   || 0,
        mech_pct:         Number(estimate.mechPct)  || 0,
        elec_pct:         Number(estimate.elecPct)  || 0,
        plumb_pct:        Number(estimate.plumbPct) || 0,
        civil_pct:        Number(estimate.civilPct)  || 0,
        admin_pct:        Number(estimate.adminPct)  || 0,
        civil_admin_pct:  Number(estimate.civilAdminPct) || 0,
        use_area:         estimate.useArea           || 'direct',
        length_ft:        Number(estimate.lengthFt)  || 0,
        width_ft:         Number(estimate.widthFt)   || 0,
        exist_sqft:       Number(estimate.existSqft) || 0,
        exist_diff:       Number(estimate.existDiff) || 0,
        rem_sqft:         Number(estimate.remSqft)   || 0,
        rem_diff:         Number(estimate.remDiff)   || 0,
        add_sqft:         Number(estimate.addSqft)   || 0,
        add_diff:         Number(estimate.addDiff)   || 0,
        civil_sqft:       Number(estimate.civilSqft) != null ? Number(estimate.civilSqft) : null,
        civil_difficulty: Number(estimate.civilDifficulty) != null ? Number(estimate.civilDifficulty) : null,
        base_price:       Number(estimate.basePrice)|| 0,
        sale_price:       Number(estimate.salePrice)|| 0,
        total_contract:   Number(estimate.totalContract) || 0,
        addons:           estimate.addons           || [],
        cuotas:           estimate.cuotas           || [],
        scope_items:      estimate.scopeItems        || [],
        terms:            (estimate.terms || []).map(t => ({ ...t, text: cleanTermText(t.text) })),
        notes:            estimate.notes            || null,
        additional_comments: estimate.additionalComments || null,
        time_frame_value: Number(estimate.timeFrameValue) || 0,
        time_frame_unit:  estimate.timeFrameUnit    || 'weeks',
        status:           estimate.status           || 'draft',
        service_groups:   estimate.serviceGroups    || [],
        cert_cost_overrides:       estimate.certCostOverrides       || {},
        direct_cost_overrides:     estimate.directCostOverrides     || {},
        commission_overrides:      estimate.commissionOverrides      || {},
        operating_cost_overrides:  estimate.operatingCostOverrides   || {},
        franchise_cost_overrides:  estimate.franchiseCostOverrides   || {},
        profit_cost_overrides:     estimate.profitCostOverrides      || {},
        contingency_cost_overrides: estimate.contingencyCostOverrides || {},
        cost_overrides:            estimate.costOverrides           || {
            direct:      estimate.directCostOverrides      || {},
            certifier:   estimate.certCostOverrides       || {},
            commission:  estimate.commissionOverrides     || {},
            operating:   estimate.operatingCostOverrides  || {},
            franchise:   estimate.franchiseCostOverrides  || {},
            profit:      estimate.profitCostOverrides     || {},
            contingency: estimate.contingencyCostOverrides || {},
        },
        min_price_override:        estimate.minPriceOverride         || null,
        discipline_deductions:     estimate.disciplineDeductions      || [],
        updated_at:       new Date().toISOString(),
    }

    if (estimate.id) {
        const { data, error } = await supabase
            .from('arch_estimates')
            .update(payload)
            .eq('id', estimate.id)
            .select()
            .single()
        if (error) throw new Error(error.message)
        await clearReferenceCache('arch_estimates_active', null).catch(() => {})
        return data
    }

    const { data, error } = await supabase
        .from('arch_estimates')
        .insert({ ...payload, created_by: userId })
        .select()
        .single()
    if (error) throw new Error(error.message)
    await clearReferenceCache('arch_estimates_active', null).catch(() => {})
    return data
}

/**
 * Flip the status of an arch estimate (e.g. draft ↔ sent).
 */
export async function markArchEstimateStatus(id, status) {
    const { error } = await supabase
        .from('arch_estimates')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
    if (error) throw new Error(error.message)
    await clearReferenceCache('arch_estimates_active', null).catch(() => {})
    await clearReferenceCache('arch_estimates_archived', null).catch(() => {})
}

/**
 * Generate a fresh arch estimate number: AYYYYMMDD-{uid}
 */
export function newArchNumber() {
    const d    = new Date()
    const yyyy = d.getFullYear()
    const mm   = String(d.getMonth() + 1).padStart(2, '0')
    const dd   = String(d.getDate()).padStart(2, '0')
    const uid  = Date.now().toString(36).toUpperCase()
    return `A${yyyy}${mm}${dd}-${uid}`
}

/**
 * Clone an existing arch estimate into a new DB record.
 * Fresh number, today's date, status reset to draft.
 */
export async function cloneArchEstimate(id, userId) {
    const { data: src, error: fetchErr } = await supabase
        .from('arch_estimates')
        .select('*')
        .eq('id', id)
        .single()
    if (fetchErr) throw new Error(fetchErr.message)
    if (!src) throw new Error('Estimate not found')

    const payload = {
        number:              newArchNumber(),
        date:                new Date().toISOString().split('T')[0],
        client:              src.client              || null,
        phone:               src.phone               || null,
        email:               src.email               || null,
        monday_item_id:      src.monday_item_id      || null,
        project_type:        src.project_type        || null,
        project_description: src.project_description || null,
        address:             src.address             || null,
        address_details:     src.address_details     || {},
        market:              src.market              || null,
        sqft:                src.sqft                || 0,
        difficulty:          src.difficulty          || 1,
        discipline_scope:    src.discipline_scope    || 'full',
        mech_pct:            src.mech_pct            || 0,
        elec_pct:            src.elec_pct            || 0,
        plumb_pct:           src.plumb_pct           || 0,
        arch_pct:            src.arch_pct            || 0,
        struct_pct:          src.struct_pct          || 0,
        mep_pct:             src.mep_pct             || 0,
        civil_pct:           src.civil_pct           || 0,
        admin_pct:           src.admin_pct           || 0,
        civil_admin_pct:     src.civil_admin_pct     || 0,
        use_area:            src.use_area            || 'direct',
        length_ft:           src.length_ft           || 0,
        width_ft:            src.width_ft            || 0,
        civil_sqft:          src.civil_sqft          ?? null,
        civil_difficulty:    src.civil_difficulty    ?? null,
        base_price:          src.base_price          || 0,
        sale_price:          src.sale_price          || 0,
        total_contract:      src.total_contract      || 0,
        addons:              src.addons              || [],
        cuotas:              src.cuotas              || [],
        scope_items:         src.scope_items         || [],
        terms:               (src.terms || []).map(t => ({ ...t, text: cleanTermText(t.text) })),
        notes:               src.notes               || null,
        additional_comments: src.additional_comments || null,
        time_frame_value:    src.time_frame_value    || 0,
        time_frame_unit:     src.time_frame_unit     || 'weeks',
        status:              'draft',
        service_groups:      src.service_groups      || [],
        cert_cost_overrides:       src.cert_cost_overrides       || {},
        direct_cost_overrides:     src.direct_cost_overrides     || {},
        commission_overrides:      src.commission_overrides      || {},
        operating_cost_overrides:  src.operating_cost_overrides  || {},
        franchise_cost_overrides:  src.franchise_cost_overrides  || {},
        profit_cost_overrides:     src.profit_cost_overrides     || {},
        contingency_cost_overrides: src.contingency_cost_overrides || {},
        discipline_deductions:     src.discipline_deductions         || [],
        created_by:          userId || null,
        updated_at:          new Date().toISOString(),
    }

    const { data, error } = await supabase
        .from('arch_estimates')
        .insert(payload)
        .select()
        .single()
    if (error) throw new Error(error.message)
    await clearReferenceCache('arch_estimates_active', null).catch(() => {})
    return data
}

/**
 * Soft-delete an arch estimate by setting status to 'archived'.
 * Tracks who archived it for the Logs panel.
 */
export async function archiveArchEstimate(id, userId, userEmail, userName) {
    const { error } = await supabase
        .from('arch_estimates')
        .update({
            status: 'archived',
            archived_by: userId,
            archived_by_email: userEmail,
            archived_by_name: userName,
            updated_at: new Date().toISOString()
        })
        .eq('id', id)
    if (error) throw new Error(error.message)
    await clearReferenceCache('arch_estimates_active', null).catch(() => {})
    await clearReferenceCache('arch_estimates_archived', null).catch(() => {})
}

/**
 * useArchivedArchEstimates — fetch all archived arch estimates with archiver info.
 */
export function useArchivedArchEstimates() {
    const [estimates, setEstimates] = useState([])
    const [loading, setLoading]   = useState(true)
    const [error,   setError]     = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const cached = await loadReferenceCache('arch_estimates_archived', null).catch(() => null)
        if (cached) {
            setEstimates(cached.data)
            setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_estimates')
            .select('*')
            .eq('status', 'archived')
            .order('updated_at', { ascending: false })
        if (err) { setError(err.message); if (!cached) setEstimates([]) }
        else     { setEstimates(data || []); await saveReferenceCache('arch_estimates_archived', data || [], null).catch(() => {}) }
        setLoading(false)
    }, [])

    useEffect(() => { load() }, [load])

    return { estimates, loading, error, reload: load }
}
